// K8 (Finar 2026-10-28) — kısmi toplam (†) hücresinin ⓘ penceresi: hangi kalemlerin / işletmelerin
// "Ölçülmüyor" olduğu için toplama girmediği ve (varsa) XXX / boş girdi uyarısı. Danışman ve portal
// ortak; kapalı girdi (`hiddenInputs` ya da sunucu maskesi `gizli_N`) ASLA kodla yazılmaz →
// `KPI_COPY.portal.notes.hiddenInput`. Yapı FormulaPopover ile aynı: portal + fixed konum, dialog,
// Esc ve ✕ kapatır; açılınca odak ✕ düğmesine, dışarı tıklama kapatır.
// Pencere ⓘ ile birlikte `<td>` içinde monte edilir; React olayları portal üzerinden React ağacında
// köpürdüğü için kökte click/dblclick/keydown/paste DURDURULUR — aksi halde ✕ tıklaması hücre
// `onClick`'ine (formül penceresi / editör), Enter/Tab tablonun klavye gezinmesine ulaşırdı.

import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { isMaskPlaceholder } from "../../lib/kpi-partial-info";
import { KPI_COPY } from "../../lib/kpi-ui";
import { placementStyle, usePopoverPlacement } from "./use-popover-placement";

export interface PartialInfoPopoverProps {
  title: string;
  refs: { code: string; label: string }[];
  entities: string[];
  hasPending: boolean;
  hiddenInputs?: string[] | undefined;
  /** müşteri yüzeyi kopyası (`KPI_COPY.portal`) — metinler aynı, kaynak ayrı (RK36) */
  client?: boolean | undefined;
  getAnchor: () => HTMLElement | null;
  onClose: () => void;
}

export function PartialInfoPopover(props: PartialInfoPopoverProps): JSX.Element {
  const { title, refs, entities, hasPending, getAnchor, onClose } = props;
  const copy = props.client ? KPI_COPY.portal : KPI_COPY.cell;
  const hidden = new Set(props.hiddenInputs ?? []);
  const rootRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const pos = usePopoverPlacement(rootRef, getAnchor, []);

  useEffect(() => {
    closeRef.current?.focus();
  }, []);

  // dışarı tıklama kapatır; ⓘ düğmesinin kendisi hariç (o zaten aç/kapa yapar)
  const latest = useRef({ onClose, getAnchor });
  latest.current = { onClose, getAnchor };
  useEffect(() => {
    function onPointerDown(e: PointerEvent): void {
      const t = e.target as Node | null;
      if (!t || rootRef.current?.contains(t) || latest.current.getAnchor()?.contains(t)) return;
      latest.current.onClose();
    }
    document.addEventListener("pointerdown", onPointerDown, true);
    return () => document.removeEventListener("pointerdown", onPointerDown, true);
  }, []);

  const stop = (e: React.SyntheticEvent): void => e.stopPropagation();
  const listStyle: React.CSSProperties = {
    margin: "4px 0 0 16px",
    lineHeight: 1.7,
    listStyle: "disc",
    color: "var(--color-text-2)",
  };

  return createPortal(
    <div
      ref={rootRef}
      role="dialog"
      aria-label={title}
      data-testid="kpi-partial-info"
      className="rounded-xl p-4 text-sm"
      data-placement={pos.placement}
      style={{
        ...placementStyle(pos),
        width: 320,
        fontFamily: "var(--font-sans)",
        background: "var(--color-surface)",
        border: "1px solid var(--color-border)",
        boxShadow: "var(--shadow-md, 0 12px 32px rgba(15,42,82,.18))",
      }}
      onClick={stop}
      onDoubleClick={stop}
      onPaste={stop}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.key === "Escape") onClose();
      }}
    >
      <div className="flex items-start gap-2 mb-2">
        <div className="font-semibold flex-1" style={{ color: "var(--color-text)" }}>
          {title} <span aria-hidden="true">†</span>
        </div>
        <button
          ref={closeRef}
          type="button"
          aria-label="Kapat"
          onClick={onClose}
          style={{ color: "var(--color-text-3)", minWidth: 28, minHeight: 28 }}
        >
          ✕
        </button>
      </div>
      {refs.length > 0 ? (
        <div className="text-xs mt-1" data-testid="kpi-partial-refs">
          <b style={{ color: "var(--color-text)" }}>{copy.partialRefs}</b>
          <ul style={listStyle}>
            {refs.map((r) => {
              const isHidden = hidden.has(r.code) || isMaskPlaceholder(r.code);
              return (
                <li key={r.code} {...(isHidden || props.client ? {} : { title: r.code })}>
                  {isHidden ? KPI_COPY.portal.notes.hiddenInput : r.label}
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
      {entities.length > 0 ? (
        <div className="text-xs mt-2" data-testid="kpi-partial-entities">
          <b style={{ color: "var(--color-text)" }}>{copy.partialEntities}</b>
          <ul style={listStyle}>
            {entities.map((name, i) => (
              <li key={`${i.toString()}:${name}`}>{name}</li>
            ))}
          </ul>
        </div>
      ) : null}
      {hasPending ? (
        <div className="text-xs mt-2" style={{ color: "var(--color-warning-text)" }}>
          {copy.partialPending}
        </div>
      ) : null}
    </div>,
    document.body,
  );
}
